import { useMemo } from 'react';

import { routes } from '@aline/routes';
import { useMatch } from 'react-router-dom';
import { usePlaybookFocus } from 'src/components/admin/playbooks/detail/suggested-edits/playbook-focus-store';
import type { Apollo } from 'src/gql/generated';

/**
 * The workflow, task, skill, playbook, or report page the user is looking at,
 * sent with each prompt so the agent acts on it. A task page sends only its
 * task: the server resolves the parent workflow from it and accepts one of
 * them, not several.
 */
export function useVisiblePageContext(): Apollo.AgentVisibleContextInput | undefined {
  const workflowPageMatch = useMatch({ end: false, path: routes.WorkflowDetail });
  const taskPageMatch = useMatch({ end: false, path: routes.WorkflowDetailTabTask });
  const skillPageMatch = useMatch({ end: false, path: routes.AdminAgentSkillDetail });
  const reportPageMatch = useMatch({ end: false, path: routes.ReportDetail });
  const taskId = taskPageMatch?.params.taskId;
  const workflowId = workflowPageMatch?.params.workflowId;
  const skillId = skillPageMatch?.params.skillId;
  const playbookId = useOpenPlaybookId();
  const publishedFocus = usePlaybookFocus();
  const reportId = reportPageMatch?.params.reportId;

  return useMemo(() => {
    if (taskId) return { taskId };
    if (workflowId) return { workflowId };
    if (skillId) return { skillId };
    if (playbookId) {
      /**
       * The page publishes a render after the URL moves, so a focus is only
       * sent for the playbook that is open now.
       */
      const playbookFocus = publishedFocus?.playbookId === playbookId ? publishedFocus.focus : null;
      return { playbookId, ...(playbookFocus ? { playbookFocus } : {}) };
    }
    if (reportId) return { reportId };
    return undefined;
  }, [playbookId, publishedFocus, reportId, skillId, taskId, workflowId]);
}

/**
 * The playbook open in the playbook editor (`/playbooks/:playbookId` and its
 * rule sub-route), so the agent knows which playbook "this playbook" or a bare
 * "add a rule" refers to. Matched against the location rather than read from
 * useParams: the drawer renders outside the page's route tree, so route params
 * are not available here.
 */
function useOpenPlaybookId() {
  const detailMatch = useMatch(routes.PlaybooksDetail);
  const ruleMatch = useMatch(routes.PlaybooksDetailRule);
  return detailMatch?.params.playbookId ?? ruleMatch?.params.playbookId ?? null;
}
